import type { Vibe } from '../zod.js';
import type { WizardOptionCopy } from './types.js';

/**
 * Read-aloud copy for each Vibe (color + typography flavor within the Nuzum
 * Method signature). Used by VibeStep + the printable client leave-behind.
 */
export const VIBE_COPY: Readonly<Record<Vibe, WizardOptionCopy>> = {
  'The Bold': {
    name: 'The Bold',
    oneLiner: 'Ember orange + Archivo Black grotesque on obsidian. Loud, honest, unfussy.',
    clientPitch:
      "This vibe doesn't ask for permission. The headline is heavy, the orange leak is unmistakable, and there's no hiding behind softness or whitespace. People scrolling will know exactly what you stand for in three seconds. It says: we're not here to be polite, we're here to be remembered.",
    bestFor: ['Trades that earn referrals', 'Bold service brands', 'Anyone fighting blandness in their category', 'Sports / fitness / performance'],
    avoidIf: ['You sell calm or quiet luxury', 'Your audience is corporate-conservative'],
    feel: ['Loud', 'Confident', 'Direct'],
  },
  'The Professional': {
    name: 'The Professional',
    oneLiner: 'Deep blue leak + Source Serif. Trust without being boring.',
    clientPitch:
      "This vibe reads as the safe choice in the best possible way. Deep navy and editorial serif say expertise; the dark obsidian base + light leaks keep it from feeling like a stock corporate template. People will trust you immediately - and then notice you're more interesting than they expected.",
    bestFor: ['Lawyers', 'Accountants', 'Financial advisors', 'B2B consultants', 'Established service firms'],
    avoidIf: ['You want to look young or scrappy', 'Your audience is rebellious / counter-culture'],
    feel: ['Trustworthy', 'Editorial', 'Established'],
  },
  'The Modern': {
    name: 'The Modern',
    oneLiner: 'Deep cyan + Plus Jakarta Sans. Fluid, technical, future-now.',
    clientPitch:
      "This is what a smart product looks like in 2026. The cyan leak feels electric without being neon, the type is geometric without being cold, and the motion gives it a sense of being alive. People will assume you have a real engineering team behind you.",
    bestFor: ['SaaS', 'Modern professional services', 'Studios', 'Tech-adjacent businesses', 'Anyone selling competence'],
    avoidIf: ['Your audience is uncomfortable with anything tech-flavored', 'You sell heritage or hand-craft'],
    feel: ['Fluid', 'Technical', 'Forward'],
  },
  'The Artisan': {
    name: 'The Artisan',
    oneLiner: 'Terracotta + Fraunces serif. Warm, lived-in, made by humans.',
    clientPitch:
      "This vibe feels like walking into a small shop where the owner roasts the beans / fires the kiln / mixes the dyes themselves. The clay-orange leaks and the warm serif say: this is craft, this is personal, this is real. People will assume there's a face and a story behind every line on the page.",
    bestFor: ['Restaurants + cafes', 'Bakeries', 'Ceramicists / makers', 'Boutique retail', 'Skilled trades that want to feel handmade'],
    avoidIf: ['You sell at scale and want to feel industrial', 'Your category demands clinical sterility'],
    feel: ['Warm', 'Lived-in', 'Handmade'],
  },
  'The Wellness': {
    name: 'The Wellness',
    oneLiner: 'Sage + dusty rose + Cormorant italic. Calm, organic, taking care.',
    clientPitch:
      "This vibe feels like walking into a quiet treatment room. The sage and dusty rose leaks are pulled from real plants and real skin tones - nothing shouts. The italic serif headlines whisper. People will feel taken care of before they read a single word.",
    bestFor: ['Spas + estheticians', 'Yoga + Pilates', 'Therapists + coaches', 'Holistic clinics', 'Skincare + wellness retail'],
    avoidIf: ['You sell tools or hardware', 'You compete on speed / aggression / urgency'],
    feel: ['Calm', 'Generous', 'Trustworthy', 'Lived-in'],
  },
  'The Editorial': {
    name: 'The Editorial',
    oneLiner: 'Editorial red on paper + Playfair Display. Magazine-grade premium.',
    clientPitch:
      "This vibe reads like a magazine spread. Big serif headlines, paper backgrounds, hairline rules, one fierce red accent that knows when to show up. It says: we have a point of view, we publish it on purpose, and we don't water it down for anybody.",
    bestFor: ['Authors + writers', 'Editorial-led brands', 'High-end consultants', 'Journalists + studios', 'Publications'],
    avoidIf: ['You need to look approachable to mass market', 'Your audience is intimidated by long-form'],
    feel: ['Premium', 'Authored', 'Deliberate'],
  },
  'The Luxe': {
    name: 'The Luxe',
    oneLiner: 'Champagne gold + Cormorant Garamond. Quiet luxury, no logos.',
    clientPitch:
      "This vibe doesn't shout luxury - it whispers it. Aged gold and bronze leaks, an elegant garamond serif, generous negative space, and zero ostentation. The kind of brand that doesn't need to tell you it's expensive because everything about it already does.",
    bestFor: ['Salons + spas at the top of the market', 'Jewelers', 'Private services', 'Boutique hotels', 'Couture / atelier'],
    avoidIf: ['You want to look approachable / mass-market', 'You need price-sensitivity messaging'],
    feel: ['Quiet', 'Considered', 'Expensive-feeling'],
  },
  'The Tech': {
    name: 'The Tech',
    oneLiner: 'Indigo + electric lavender + Space Grotesk. Engineered with personality.',
    clientPitch:
      "This vibe reads as a product team that ships. Indigo and lavender leaks feel electric without being a meme; geometric grotesque type says we think in systems. It's tech without the soulless corporate-SaaS gloss.",
    bestFor: ['B2B SaaS', 'Developer tools', 'Tech agencies', 'Engineering-led services', 'Anything API-shaped'],
    avoidIf: ['Your category actively distrusts tech-flavored brands', 'You need to feel old-world'],
    feel: ['Engineered', 'Sharp', 'Optimistic'],
  },
  'The Heritage': {
    name: 'The Heritage',
    oneLiner: 'Burgundy + forest + Crimson Pro. Old-world craft on modern bones.',
    clientPitch:
      "This vibe says we've been doing this for longer than the competition. Burgundy and forest leaks feel like deep wood and aged leather; Crimson Pro headlines have an Oxford-press weight. But the dark base and clean composition keep it from looking like a Victorian relic - it's heritage on modern bones.",
    bestFor: ['Established trades', 'Family businesses', 'Legal + financial', 'Distilleries + wineries', 'Anything multi-generational'],
    avoidIf: ['You are brand new and need to feel young', 'You sell tech or speed'],
    feel: ['Established', 'Crafted', 'Considered'],
  },
};
